import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { T, t } from '@sm/intl';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Input } from '@wds/input';
import { Modal } from '@wds/modal';
import { Typography } from '@wds/typography';
import { trackAmplitudeEvent } from '../../../lib';
import { validateTheme, NO_ERRORS } from '../../../validators';
import ThemeGuidanceCard from '../ThemeGuidanceCard';
import InputSkeleton from './InputSkeleton';
import { useThemes, useGeneral } from '../../../Context';
export const COPY = {
  header: {
    id: 'OpenEndedCoding.EditThemeModal.header',
    defaultMessage: 'Edit Theme',
    description: '[Type: Header][Vis: med] - Header for Edit Theme Modal'
  },
  cancel: {
    id: 'OpenEndedCoding.EditThemeModal.cancel',
    defaultMessage: 'Cancel',
    description: '[Type: Header][Vis: med] - Edit Theme Modal cancel button'
  },
  save: {
    id: 'OpenEndedCoding.EditThemeModal.save',
    defaultMessage: 'Save',
    description: '[Type: Header][Vis: med] - Edit Theme Modal delete button'
  },
  theme: {
    id: 'OpenEndedCoding.EditThemeModal.theme',
    defaultMessage: 'Name',
    description: '[Type: Header][Vis: med] - Edit Theme Modal theme title'
  },
  keywords: {
    id: 'OpenEndedCoding.EditThemeModal.keywords',
    defaultMessage: 'Keywords',
    description: '[Type: Header][Vis: med] - Edit Theme Modal keywords title'
  },
  updateThemeLabel: {
    id: 'OpenEndedCoding.EditThemeModal.updateThemeLabel',
    defaultMessage: 'Update a theme',
    description: '[Type: Header][Vis: med] - Edit Theme Modal update theme label'
  },
  getKeywordsError: {
    id: 'OpenEndedCoding.EditThemeModal.getKeywordsError',
    defaultMessage: 'An error occured getting the keywords',
    description: '[Type: Header][Vis: med] - Edit Theme Modal get keywords error'
  }
};
const EditThemeModal = ({
  show,
  selectedThemeId,
  onClose,
  onSave
}) => {
  var _a;
  const {
    themes,
    keywords,
    keywordsLoading,
    keywordsError
  } = useThemes();
  const {
    surveyId,
    questionId,
    isSolutions
  } = useGeneral();
  const selectedTheme = themes.find(theme => theme.id === selectedThemeId);
  const selectedKeywords = keywords.find(themeWithKeyword => themeWithKeyword.themeId === selectedThemeId);
  const originalTheme = selectedTheme ? selectedTheme.theme : '';
  const [theme, setTheme] = useState(originalTheme);
  const [error, setError] = useState(NO_ERRORS);
  const touched = useRef(false);
  const handleThemeChange = e => {
    const themeName = e.currentTarget.value;
    setTheme(themeName);
    if (touched.current) {
      setError(validateTheme({
        themes,
        themeName,
        originalTheme
      }));
    }
  };
  useEffect(() => {
    setTheme(selectedTheme ? selectedTheme.theme : '');
  }, [selectedTheme]);
  const handleClose = () => {
    onClose();
    setError(NO_ERRORS);
    setTheme('');
  };
  return _jsxs(Modal, {
    "data-testid": "EditThemeModal__OEC",
    show: show,
    onClose: handleClose,
    size: "md",
    children: [_jsx(Modal.Header, {
      header: t(COPY.header)
    }), _jsxs(Modal.Body, {
      children: [_jsx(Box, {
        mb: 5,
        children: _jsx(Input, Object.assign({
          ariaLabel: t(COPY.updateThemeLabel),
          stretched: true,
          label: t(COPY.theme),
          value: theme,
          onChange: handleThemeChange
        }, error.error && {
          color: 'warning',
          errorMessage: _jsx(Box, {
            children: error.messages.map(message => _jsx(Typography, {
              component: "p",
              variant: "bodySm",
              color: "warning",
              children: message
            }, message))
          })
        }))
      }), _jsx(Box, {
        mb: 5,
        children: keywordsLoading ? _jsx(InputSkeleton, {}) : _jsx(Input, Object.assign({
          stretched: true,
          disabled: true,
          label: t(COPY.keywords),
          value: (_a = selectedKeywords === null || selectedKeywords === void 0 ? void 0 : selectedKeywords.keywords.join(', ')) !== null && _a !== void 0 ? _a : ''
        }, keywordsError && {
          color: 'warning',
          errorMessage: _jsx(Box, {
            children: _jsx(Typography, {
              component: "p",
              variant: "bodySm",
              color: "warning",
              children: t(COPY.getKeywordsError)
            })
          })
        }))
      }), _jsx(ThemeGuidanceCard, {})]
    }), _jsxs(Modal.Footer, {
      align: "right",
      children: [_jsx(Button, {
        variant: "ghost",
        color: "secondary",
        onClick: handleClose,
        children: _jsx(T, {
          desc: COPY.cancel
        })
      }), _jsx(Button, {
        disabled: error.error || theme === originalTheme,
        onClick: () => {
          const validated = validateTheme({
            themes,
            themeName: theme,
            originalTheme
          });
          if (validated.error) {
            setError(validated);
            touched.current = true;
          } else {
            onSave(theme);
            onClose();
          }
          // tracking
          trackAmplitudeEvent({
            amplitudeEvent: 'open-ended coding edited theme',
            data: {
              surveyId,
              questionId,
              pageName: isSolutions ? 'ctm' : 'analyze'
            }
          });
        },
        children: _jsx(T, {
          desc: COPY.save
        })
      })]
    })]
  });
};
export default EditThemeModal;